import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { webURL } from "../../../../../services/API";
import axios from "axios";
import { AuthenticateSelect } from "../../../../auth/select";
import { getGoogleAccountToken } from "../../../../auth/method";
import { TextInput } from "../../../../form/textInput";
import { updateNodeValue } from "../../../../../services/method";
import { Loader, Main, Styled } from "./style";
import { Select } from "../../../../form/select";

type GoogleCalendarTriggerProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function GoogleCalendarTrigger({
   id,
   data,
   setChildMinimize,
   setChildSettings,
}: GoogleCalendarTriggerProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({
      isToggle: false,
   });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [webhookUrl, setWebhookUrl] = React.useState<string>("");
   const [isLoading, setIsLoading] = React.useState<boolean>(false);
   const [isWatching, setIsWatching] = React.useState<boolean>(false);
   const [watchStatus, setWatchStatus] = React.useState<string>("");

   React.useEffect(() => {
      if (setChildMinimize)
         setChildMinimize(() => {
         setIsExpanded((prev) => !prev);
         });
      if (setChildSettings)
         setChildSettings(() => {
         setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // Initialize webhook URL with projectId and nodeId in the URL
   React.useEffect(() => {
      // Get projectId from URL
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get("id");

      // Build webhook URL with projectId and nodeId as URL parameters
      const url = `${webURL}/triggers/calendar/notifications/${projectId}/${id}?blockId=${id}`;
      console.log("Webhook URL:", url);
      setWebhookUrl(url);

      // Check if already watching
      if (data.value?.watchChannel?.id) {
         setIsWatching(true);
         setWatchStatus(
         `Watching calendar (Channel: ${data.value.watchChannel.id})`
         );
      }
   }, [data.value?.watchChannel, id]);

   // Handle authentication
   const handleAuthenticate = async (type: "add" | "remove", authData: any) => {
      if (type === "add") {
         const token = await getGoogleAccountToken(authData, id, setNodes);

         // Fetch calendar list after authentication
         try {
         const response = await axios.get(
            `${webURL}/blocks/google_calendar_list`,
            {
               headers: {
               Authorization: `Bearer ${token}`,
               },
            }
         );

         if (response.data.success) {
            let calendars = response.data.calendars.map(
               (cal: any, index: number) => ({
               id: cal.id,
               name: cal.summary || cal.id,
               isActive: index === 0,
               })
            );
            // Only show calendars with @gmail.com in their id
            const gmailCalendars = calendars.filter((cal: any) =>
               cal.id.includes("@gmail.com")
            );
            calendars = gmailCalendars.length > 0 ? [gmailCalendars[0]] : [];

            updateNodeValue(id, setNodes, {
               connection: authData,
               calendars: calendars,
               calendarId: calendars[0]?.id || "primary",
            });
         }
         } catch (error) {
         updateNodeValue(id, setNodes, {
            connection: authData,
            calendarId: "primary",
         });
         }
      } else if (type === "remove") {
         updateNodeValue(id, setNodes, {
         connection: null,
         calendars: [],
         watchChannel: null,
         syncToken: null,
         });
         setIsWatching(false);
         setWatchStatus("");
      }
   };
   const handleSelectCalendars = (selectId: number | string) => {
      setNodes((nodes) =>
         nodes.map((node) => {
         if (node.id === id) {
            const updatedCalendars = node.data.value.calendars.map(
               (item: any) => {
               const isActive = item.id === selectId;

               return {
                  ...item,
                  isActive,
               };
               }
            );

            return {
               ...node,
               data: {
               ...node.data,
               value: {
                  ...node.data.value,
                  calendars: updatedCalendars,
                  calendarId: selectId, // Store the selected calendar ID directly
               },
               },
            };
         } else return node;
         })
      );
   };
   // Handle input changes
   const handleChangeInput = (
      e: React.ChangeEvent<HTMLInputElement>,
      keyRef?: string
   ) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   };

   // Start watching for calendar changes
   const handleStartWatch = async () => {
      if (!data.value?.connection) {
         alert("Please connect your Google account first");
         return;
      }

      setIsLoading(true);
      setWatchStatus("Setting up watch...");

      try {
         const token = await getGoogleAccountToken(
         data.value.connection,
         id,
         setNodes
         );
         const calendarId = data.value.calendarId || "primary";

         // Get projectId from URL
         const urlParams = new URLSearchParams(window.location.search);
         const projectId = urlParams.get("id");

         if (!projectId) {
         throw new Error("Project ID not found in URL");
         }

         // Call the watch_events endpoint
         const response = await axios.post(
         `${webURL}/blocks/google_calendar_watch`,
         {
            calendarId,
            webhookUrl,
            ttl: data.value?.ttl || "60480", // 7 days default
            projectId: projectId,
            nodeId: id,
            nodeIndex: data.index,
         },
         {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         }
         );

         if (response.data.success) {
         const watchData = response.data.watchChannel;

         // Update node with watch channel info and sync token
         updateNodeValue(id, setNodes, {
            watchChannel: watchData,
            syncToken: response.data.syncToken,
            // lastWatchTime: new Date().toISOString(),
         });

         setIsWatching(true);
         setWatchStatus(`✅ Watching calendar (Channel: ${watchData.id})`);
         } else {
         setWatchStatus("❌ Failed to start watch");
         }
      } catch (error: any) {
         console.error("Watch error:", error);
         setWatchStatus(
         `❌ Error: ${error.response?.data?.message || error.message}`
         );
      } finally {
         setIsLoading(false);
      }
   };

   // Stop watching for calendar changes
   const handleStopWatch = async () => {
      if (!data.value?.watchChannel) {
         alert("No active watch to stop");
         return;
      }

      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure you want to stop watching this calendar?")) {
         return;
      }

      setIsLoading(true);
      setWatchStatus("Stopping watch...");

      try {
         const { watchChannel, connection } = data.value;

         // Get projectId from URL
         const urlParams = new URLSearchParams(window.location.search);
         const projectId = urlParams.get("id");

         if (!projectId) {
         throw new Error("Project ID not found in URL");
         }

         // Call the stop watch endpoint
         const response = await axios.post(
         `${webURL}/blocks/google_calendar_stop_watch`,
         {
            nodeId: id,
            projectId: projectId,
            connectionId: connection,
            // Also send the direct values as fallback
            channelId: watchChannel.channelId || watchChannel.id,
            resourceId: watchChannel.resourceId,
         }
         );

         if (response.data.success) {
         // Clear watch channel info and sync token from node
         updateNodeValue(id, setNodes, {
            watchChannel: null,
            syncToken: null,
         });

         setIsWatching(false);
         setWatchStatus("✅ Watch stopped successfully");
         } else {
         setWatchStatus("❌ Failed to stop watch");
         }
      } catch (error: any) {
         console.error("Stop watch error:", error);
         setWatchStatus(
         `❌ Error: ${error.response?.data?.message || error.message}`
         );
      } finally {
         setIsLoading(false);
      }
   };

   const isWarning = !data.value?.connection;

   return (
      <>
         <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
         <Main>
         {isWarning ? (
            <>
               <div id="warning"></div>
               <span id="warningIcon">
               <IoWarningOutline size={21} />
               </span>
            </>
         ) : data.output ? (
            <>
               <div id="ran"></div>
               <span id="ranIcon">
               <IoCheckmarkCircleOutline size={21} />
               </span>
            </>
         ) : null}
         {isExpanded ? (
            <ExpandedBlock
               data={{ id, ...data }}
               handleClose={() => setIsExpanded(false)}
               isWarning={isWarning}
               style={{ minWidth: "420px" }}>
               {isLoading ? (
               <Loader />
               ) : (
               <Styled>
                  {/* Google Authentication */}
                  <AuthenticateSelect
                     type="google"
                     defaultId={data.value?.connection?.id}
                     handleAuthenticate={handleAuthenticate}
                     background={data.background}
                  />

                  <div className="action" />

                  {/* Calendar Selection - Show dropdown if calendars available, otherwise text input */}
                  {data.value?.calendars?.length > 0 ? (
                     <Select
                     title="Calendar"
                     data={data.value.calendars}
                     handleSelectItem={(id) => handleSelectCalendars(id)}
                     isSmall={true}
                     />
                  ) : (
                     <TextInput
                     keyRef="calendarId"
                     value={data.value?.calendarId || "primary"}
                     title="Calendar ID"
                     placeholder="primary"
                     handleChange={handleChangeInput}
                     />
                  )}
                  {/* TTL (Time to Live) */}
                  <TextInput
                     keyRef="ttl"
                     value={data.value?.ttl || "604800"}
                     title="Watch (Sec)"
                     placeholder="604800 (7 days)"
                     handleChange={handleChangeInput}
                  />

                  {/* Start Watch Button */}
                  <div className="watch-section">
                     <button
                     className="watch-button btn1"
                     onClick={handleStartWatch}
                     disabled={isLoading || !data.value?.connection}>
                     <FontAwesomeIcon icon={faSync} spin={isLoading} />
                     {isWatching ? " Renew Watch" : " Start Watching"}
                     </button>

                     {/* Stop Watch Button */}
                     {isWatching && data.value?.watchChannel && (
                     <button
                        className="stop-watch-button btn1"
                        onClick={handleStopWatch}
                        disabled={isLoading}>
                        <FontAwesomeIcon icon={faSync} />
                        {" Stop Watch"}
                     </button>
                     )}

                     {watchStatus && (
                     <div
                        className={`watch-status ${
                           isWatching ? "success" : "error"
                        }`}>
                        {watchStatus}
                     </div>
                     )}
                  </div>

                  {/* Watch Info Display */}
                  {data.value?.watchChannel && (
                     <div className="watch-info">
                     <h5>Watch Details:</h5>
                     <ul>
                        <li>
                           <strong>Channel ID:</strong>{" "}
                           {data.value.watchChannel.id}
                        </li>
                        <li>
                           <strong>Resource ID:</strong>{" "}
                           {data.value.watchChannel.resourceId}
                        </li>
                        <li>
                           <strong>Expiration:</strong>{" "}
                           {new Date(
                           parseInt(data.value.watchChannel.expiration)
                           ).toLocaleString()}
                        </li>
                        {data.value.lastWatchTime && (
                           <li>
                           <strong>Started:</strong>{" "}
                           {new Date(data.value.lastWatchTime).toLocaleString()}
                           </li>
                        )}
                     </ul>
                     </div>
                  )}
               </Styled>
               )}
            </ExpandedBlock>
         ) : null}
         </Main>
      </>
   );
}